import { isAttachmentUrl } from '@tms/shared';

/**
 * A deliberately small Markdown subset — enough to structure a test case
 * (headings, lists, quotes, code, screenshots) and nothing else.
 *
 * The text is parsed into a block tree that the renderer turns into React
 * elements. Nothing is ever handed to `dangerouslySetInnerHTML`, so a case
 * body cannot inject markup, and the only images that render are the ones this
 * application serves itself.
 */
export type InlineNode =
  | { kind: 'text'; text: string }
  | { kind: 'strong'; text: string }
  | { kind: 'em'; text: string }
  | { kind: 'code'; text: string }
  | { kind: 'link'; text: string; href: string }
  | { kind: 'image'; alt: string; src: string };

export type Block =
  | { kind: 'paragraph'; content: InlineNode[] }
  | { kind: 'heading'; level: 1 | 2 | 3; content: InlineNode[] }
  | { kind: 'code'; language: string | null; code: string }
  | { kind: 'list'; ordered: boolean; items: InlineNode[][] }
  | { kind: 'quote'; content: InlineNode[] };

const INLINE_PATTERN =
  /(!\[[^\]]*\]\([^)\s]+\)|\[[^\]]+\]\([^)\s]+\)|`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*)/g;

/** Links are limited to http(s) and to this application's own paths. */
const safeHref = (href: string): string | null => {
  if (/^https?:\/\//i.test(href)) return href;
  if (href.startsWith('/') && !href.startsWith('//')) return href;
  return null;
};

export const parseInline = (text: string): InlineNode[] => {
  const nodes: InlineNode[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(INLINE_PATTERN)) {
    const token = match[0];
    const start = match.index ?? 0;
    if (start > lastIndex) {
      nodes.push({ kind: 'text', text: text.slice(lastIndex, start) });
    }
    lastIndex = start + token.length;

    if (token.startsWith('![')) {
      const [, alt = '', src = ''] = /^!\[([^\]]*)\]\(([^)\s]+)\)$/.exec(token) ?? [];
      // Only attachments this API serves are rendered as images; anything else
      // stays visible as text rather than loading from a third party.
      if (isAttachmentUrl(src)) nodes.push({ kind: 'image', alt, src });
      else nodes.push({ kind: 'text', text: token });
      continue;
    }
    if (token.startsWith('[')) {
      const [, label = '', href = ''] = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(token) ?? [];
      const safe = safeHref(href);
      if (safe) nodes.push({ kind: 'link', text: label, href: safe });
      else nodes.push({ kind: 'text', text: token });
      continue;
    }
    if (token.startsWith('`')) {
      nodes.push({ kind: 'code', text: token.slice(1, -1) });
      continue;
    }
    if (token.startsWith('**')) {
      nodes.push({ kind: 'strong', text: token.slice(2, -2) });
      continue;
    }
    nodes.push({ kind: 'em', text: token.slice(1, -1) });
  }

  if (lastIndex < text.length) {
    nodes.push({ kind: 'text', text: text.slice(lastIndex) });
  }
  return nodes;
};

const LIST_ITEM = /^[-*]\s+(.*)$/;
const ORDERED_ITEM = /^\d+[.)]\s+(.*)$/;
const HEADING = /^(#{1,3})\s+(.*)$/;
const QUOTE = /^>\s?(.*)$/;
const FENCE = /^```\s*([A-Za-z0-9+#-]*)\s*$/;

export const parseMarkdown = (source: string): Block[] => {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let paragraph: string[] = [];

  const flushParagraph = (): void => {
    if (paragraph.length === 0) return;
    blocks.push({ kind: 'paragraph', content: parseInline(paragraph.join('\n')) });
    paragraph = [];
  };

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index] ?? '';

    const fence = FENCE.exec(line);
    if (fence) {
      flushParagraph();
      const code: string[] = [];
      index += 1;
      while (index < lines.length && !FENCE.test(lines[index] ?? '')) {
        code.push(lines[index] ?? '');
        index += 1;
      }
      blocks.push({ kind: 'code', language: fence[1] ? fence[1] : null, code: code.join('\n') });
      continue;
    }

    if (line.trim().length === 0) {
      flushParagraph();
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      flushParagraph();
      blocks.push({
        kind: 'heading',
        level: heading[1]?.length as 1 | 2 | 3,
        content: parseInline(heading[2] ?? ''),
      });
      continue;
    }

    const quote = QUOTE.exec(line);
    if (quote) {
      flushParagraph();
      blocks.push({ kind: 'quote', content: parseInline(quote[1] ?? '') });
      continue;
    }

    const bullet = LIST_ITEM.exec(line);
    const ordered = ORDERED_ITEM.exec(line);
    if (bullet || ordered) {
      flushParagraph();
      const isOrdered = Boolean(ordered);
      const items: InlineNode[][] = [];
      while (index < lines.length) {
        const current = lines[index] ?? '';
        const match = isOrdered ? ORDERED_ITEM.exec(current) : LIST_ITEM.exec(current);
        if (!match) break;
        items.push(parseInline(match[1] ?? ''));
        index += 1;
      }
      index -= 1;
      blocks.push({ kind: 'list', ordered: isOrdered, items });
      continue;
    }

    paragraph.push(line);
  }

  flushParagraph();
  return blocks;
};

/** True when the value has no visible content — used to show the empty state. */
export const isBlank = (value: string | null | undefined): boolean =>
  value === null || value === undefined || value.trim().length === 0;

/**
 * The visible words of a Markdown value, with the markers dropped.
 *
 * Compact places — a row in a run's check list, a dialog subtitle — need one
 * line of text, not a rendered document. Images contribute their alt text,
 * code its content: what a reader would say out loud.
 */
export const toPlainText = (source: string): string => {
  const fromInline = (nodes: InlineNode[]): string =>
    nodes.map((node) => (node.kind === 'image' ? node.alt : node.text)).join('');

  return parseMarkdown(source)
    .map((block) => {
      if (block.kind === 'code') return block.code;
      if (block.kind === 'list') return block.items.map(fromInline).join(' ');
      return fromInline(block.content);
    })
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
};
